import * as AppleAuthentication from 'expo-apple-authentication';
import { View, Platform, StyleSheet, Pressable, Animated } from 'react-native';
import { Text } from 'tamagui';
import { DefaultRootState } from '@reduxLocal/persistState';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import DeviceInfo from 'react-native-device-info';
import AsyncStorage from '@react-native-async-storage/async-storage';
import getTCConfirm from '@components/modals/TCModal/utils/getTCConfirm';
import {
  INITIALISE_MENU,
  SET_SKIP_NATIVE_LOGIN_SESSION_FETCH,
  SHOW_AUTH_MODAL,
  SHOW_OTP,
  SHOW_TC_MODAL,
  SUCCESS,
} from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';
import { useNavigation } from '@react-navigation/native';
import {
  computePostOAuthIsNewUser,
  dispatchPostAuthEasyModeOnboarding,
  handleTermsAndConditions,
  handleUserData,
  replaceLoginStackWithDashboard,
} from '@navigation/screens/TopLevel/Components/Authorisation/utils/authUtils';
import { trackSignupComplete } from '@utils/conversionAnalytics';
import { AntDesign } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { getAuthSessionGuard } from '@utils/authSessionGuard';
import { syncWatchSessionLater } from '@services/watchSessionSync';
import { useTranslation } from 'react-i18next';
import { getEmailFromAppleIdentityToken } from '@utils/getEmailFromAppleIdentityToken';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import styles from '@stylesheet';

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();

// TypeScript declarations for Apple Sign In Web SDK
declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: {
            code: string;
            id_token: string;
            state: string;
          };
          user?: {
            email?: string;
            name?: {
              firstName?: string;
              lastName?: string;
            };
          };
        }>;
      };
    };
  }
}

// Apple Web Client ID - Replace with your actual Apple Client ID from Apple Developer Console
const APPLE_WEB_CLIENT_ID = process.env.EXPO_PUBLIC_APPLE_WEB_CLIENT_ID || 'com.smartsaas.signin';

const AppleButton = ({ code, productId }: { code?: string; productId?: string }) => {
  const { t } = useTranslation();
  const theme = !themeSelector();
  const buttonColors = {
    background: theme ? '#ffffff' : 'rgba(30, 41, 59, 0.95)',
    text: theme ? '#111827' : '#e2e8f0',
    border: theme ? '#e5e7eb' : 'rgba(148, 163, 184, 0.35)',
    hover: theme ? 'rgba(90,192,234,0.25)' : 'rgba(59,130,246,0.2)',
    shadow: theme ? '#000000' : '#020617',
  };
  const navigation = useNavigation();
  const notificationToken = useSelector(
    (state: DefaultRootState) => state.app.notificationToken
  );
  const dispatch = useDispatch();
  // Animation for code indicator
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Animation effect for code indicator
  useEffect(() => {
    if (code) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }).start();
    }
  }, [code, fadeAnim]);

  // Check platform capability for Apple Sign In
  const [isAppleAvailable, setIsAppleAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAppleAvailability = async () => {
      try {
        if (Platform.OS === 'ios') {
          const available = await AppleAuthentication.isAvailableAsync();
          setIsAppleAvailable(available);
        } else if (Platform.OS === 'web') {
          // For web, we'll show a custom styled button that explains Apple Sign In
          setIsAppleAvailable(true);
        } else {
          setIsAppleAvailable(false);
        }
      } catch (error) {
        console.log('Apple Sign In availability check failed:', error);
        setIsAppleAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAppleAvailability();
  }, []);

  // Load Apple's Sign In JS SDK for web
  const loadAppleSignInScript = () => {
    return new Promise((resolve, reject) => {
      if (Platform.OS !== 'web') {
        resolve(true);
        return;
      }
      
      if (window.AppleID) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.onload = () => {
        window.AppleID.auth.init({
          clientId: APPLE_WEB_CLIENT_ID,
          scope: 'name email',
          redirectURI: window.location.origin + '/auth/apple/callback',
          state: 'apple-signin',
          usePopup: true
        });
        resolve(true);
      };
      script.onerror = () => reject(new Error('Failed to load Apple Sign In script'));
      document.head.appendChild(script);
    });
  };

  // Handle Apple Sign In for different platforms
  const handleAppleSignIn = async () => {
    // Check if another auth session is active
    if (authGuard.isSessionActive()) {
      const activeSession = authGuard.getActiveSession();
      console.warn(`Cannot start Apple auth - ${activeSession} is already active`);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Please wait for ${activeSession} authentication to complete`,
          type: 'error'
        }
      });
      return;
    }

    try {
      if (Platform.OS === 'ios' && isAppleAvailable) {
        // Native iOS Apple Sign In with guard
        const credential = await authGuard.startSession('Apple', async () => {
          return await AppleAuthentication.signInAsync({
            requestedScopes: [
              AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
              AppleAuthentication.AppleAuthenticationScope.EMAIL,
            ],
          });
        });

        const emailFromToken = getEmailFromAppleIdentityToken(credential.identityToken);
        const emailFromCredential =
          typeof credential.email === 'string' && credential.email.trim().includes('@')
            ? credential.email.trim()
            : null;
        // Apple only returns credential.email on first consent; JWT may still carry `email`.
        const resolvedEmail = emailFromCredential ?? emailFromToken ?? null;
        const newOAuthUser = {
          surname: credential.fullName?.familyName,
          name: credential.fullName?.givenName,
          authId: credential.user,
          email: resolvedEmail,
          oauthToken: credential.identityToken,
          type: 'apple',
          device: {
            brand: DeviceInfo.getBrand(),
            model: DeviceInfo.getModel(),
            os: DeviceInfo.getSystemName(),
            osVersion: DeviceInfo.getSystemVersion(),
          },
        };

        await processOAuthResponse(newOAuthUser);
      } else if (Platform.OS === 'web') {
        // Web Apple Sign In using Apple's JS SDK
        try {
          if (APPLE_WEB_CLIENT_ID === 'com.smartsaas.signin') {
            handleError('Apple Sign In requires configuration. Please set up your Apple Web Client ID in the environment variables.');
            return;
          }

          await loadAppleSignInScript();
          
          const response = await window.AppleID.auth.signIn();
          
          if (response.authorization && response.authorization.id_token) {
            const fromUser =
              typeof response.user?.email === 'string' &&
              response.user.email.trim().includes('@')
                ? response.user.email.trim()
                : null;
            const webEmail =
              fromUser ||
              getEmailFromAppleIdentityToken(response.authorization.id_token);
            const newOAuthUser = {
              surname: response.user?.name?.lastName || null,
              name: response.user?.name?.firstName || null,
              authId: response.authorization.code,
              email: webEmail || null,
              oauthToken: response.authorization.id_token,
              type: 'apple',
              device: {
                brand: 'Web',
                model: navigator.userAgent,
                os: 'Web',
                osVersion: navigator.appVersion,
              },
            };

            await processOAuthResponse(newOAuthUser);
          } else {
            handleError('Apple Sign In failed: No authorization received');
          }
        } catch (webError) {
          console.error('Web Apple Sign In error:', webError);
          if (webError.error === 'popup_closed_by_user') {
            return;
          } else {
            handleError('Apple Sign In is temporarily unavailable. Please try another login method.');
          }
        }
      } else {
        handleError('Apple Sign In is not supported on this platform.');
      }
    } catch (e) {
      if (e.code === 'ERR_REQUEST_CANCELED') {
      } else {
        console.log(e);
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: e.toString(), type: 'error' } });
      }
    }
  };

  // Process OAuth response
  const processOAuthResponse = async (newOAuthUser) => {
    const res = await apis.handleOAuth(newOAuthUser);
    if (res.status === 202) {
      if (res.data.terms) {
        console.log("user has terms and conditions to accept");
        dispatch({ type: SHOW_TC_MODAL, payload: res.data.userId });

        try {
          const confirm = await getTCConfirm();
          console.log('received a response from the TC Confirmation', confirm);
          dispatch({ type: SHOW_OTP, payload: res.data });
        } catch (e) {
          handleError('Please accept our terms of service to continue');
        }
      } else {
        dispatch({ type: SHOW_OTP, payload: res.data });
      }
    } else {
      try {
        await handleTermsAndConditions(res, dispatch);
      } catch (e) {
        if (e?.message === 'TC_MODAL_CANCELLED') {
          return;
        }
        return;
      }

      const termsJustAccepted = !!res.data?.terms;
      if (termsJustAccepted) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      const amendedData = await handleUserData(res, notificationToken);

      if (!amendedData?.userData?.id) {
        handleError('Authentication succeeded but user data is incomplete. Please try again.');
        return;
      }

      dispatch({ type: SUCCESS, data: amendedData });

      try {
        await AsyncStorage.setItem('userData', JSON.stringify(amendedData));
      } catch (storageError) {
        console.warn('[AppleButton] Failed to save session to storage:', storageError);
      }

      if (Platform.OS === 'web') {
        dispatch({ type: INITIALISE_MENU });
      }

      const isNewUser = computePostOAuthIsNewUser(amendedData);

      const tcModalVisible = store.getState()?.modals?.tcModal?.modalDisplayed;
      if (tcModalVisible && !termsJustAccepted) {
        console.log('[AppleButton] TC modal still visible, skipping dashboard replace');
        return;
      }

      if (Platform.OS !== 'web') {
        dispatch({ type: SET_SKIP_NATIVE_LOGIN_SESSION_FETCH, payload: true });
        syncWatchSessionLater({
          userId: amendedData.userData.id,
          companyId: amendedData?.company?._id || amendedData?.company?.id || null,
          companyName:
            amendedData?.company?.company_name ||
            amendedData?.company?.copmany_name ||
            null,
          targetUserId:
            amendedData?.targetUser?._id || amendedData?.targetUser?.id || null,
        });
      }

      const navigationDelay = termsJustAccepted ? 300 : 0;

      setTimeout(() => {
        if (isNewUser) {
          void trackSignupComplete({ method: 'apple', source: 'oauth' });
          dispatchPostAuthEasyModeOnboarding(dispatch);
          replaceLoginStackWithDashboard(navigation, true, amendedData);
        } else {
          replaceLoginStackWithDashboard(navigation, false, amendedData);
        }
      }, navigationDelay);
    }
  };
  // Centralized error handler
  function handleError(message) {
    console.error(message);
    setTimeout(() => dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: message, type: 'error' }
    }), 1000);
  }

  // Don't render if loading or not available
  if (isLoading) {
    return null;
  }

  if (!isAppleAvailable && Platform.OS !== 'web') {
    return null;
  }

  const appleNativeButtonStyle = theme
    ? AppleAuthentication.AppleAuthenticationButtonStyle.BLACK
    : AppleAuthentication.AppleAuthenticationButtonStyle.WHITE;

  const oauthPillOuterStyle = {
    minHeight: 50,
    height: 50,
    width: '100%' as const,
    maxWidth: Platform.OS === 'web' ? 400 : undefined,
    alignSelf: 'center' as const,
    flexGrow: 0,
    flexShrink: 0,
    elevation: 1,
    shadowRadius: 10,
    shadowOpacity: 0.2,
    backgroundColor: buttonColors.background,
    borderRadius: 30,
    flexDirection: 'row' as const,
    overflow: 'hidden' as const,
    justifyContent: 'center' as const,
    alignContent: 'center' as const,
    alignItems: 'stretch' as const,
  };

  // Render platform-specific button
  if (Platform.OS === 'ios' && isAppleAvailable) {
    return (
      <View style={oauthPillOuterStyle}>
        <View style={{ flex: 1 }}>
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}
          <AppleAuthentication.AppleAuthenticationButton
            buttonType={AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN}
            buttonStyle={appleNativeButtonStyle}
            cornerRadius={15}
            style={{ flex: 1 }}
            onPress={handleAppleSignIn}
          />
        </View>
      </View>
    );
  }

  // Web and desktop fallback — same container pattern as GoogleButtonLogin
  return (
    <Pressable onPress={handleAppleSignIn} style={oauthPillOuterStyle}>
      {(state) => {
        const hovered = Boolean((state as { hovered?: boolean }).hovered);
        return (
        <View style={{ flex: 1 }}>
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}
          <View
            style={{
              paddingHorizontal: '10%',
              flex: 1,
              flexDirection: 'row',
              backgroundColor: hovered ? buttonColors.hover : 'rgba(90,192,234,0)',
              borderRadius: 10,
            }}
          >
            <View style={[styles.centerEverything, { flex: 1, maxWidth: 40 }]}>
              <AntDesign name="apple" size={20} color={buttonColors.text} />
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: Platform.OS === 'web' ? 0 : 220,
              }}
            >
              <Text
                adjustsFontSizeToFit
                allowFontScaling={false}
                style={{ fontSize: responsiveFontSize(15), color: buttonColors.text, fontWeight: '600' }}
              >
                {t('Continue with Apple')}
              </Text>
            </View>
          </View>
        </View>
        );
      }}
    </Pressable>
  );
}
export default AppleButton